import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './ManageExtension.styles';
import CohortStudentsList from './CohortStudentsList';
import ExistingSubmissionErrorDialog from '../../components/ExistingSubmissionErrorDialog';
import ExtensionPropertiesStep from './ExtensionPropertiesStep';
import ExtensionStudentsStep from './ExtensionStudentsStep';
import {
  ExtensionAssignment,
  ExtensionOperationError,
  ExtensionStudent,
  ExtensionUser,
  ManageExtensionStep,
} from './ManageExtension.types';
import TurnitinExtensionModal from './TurnitinExtensionModal';
import DeleteExtensionModal from './DeleteExtension';
import {
  getConflictStudentIds,
  getConflictStudents,
} from './submissionConflict';
import { createStyles as createDeleteStyles } from '../SubmissionsSidebar/SubmissionsSidebar.styles';

type Props = {
  accessToken?: string;
  assignment: ExtensionAssignment;
  assignmentCreationError?: ExtensionOperationError;
  assignmentUpdateError?: ExtensionOperationError;
  createExtension: (params: any) => Promise<ExtensionAssignment | null>;
  editExtension: (params: any) => Promise<ExtensionAssignment | null>;
  extension?: ExtensionAssignment;
  isOpen: boolean;
  loadStudents: (spaceId: string) => Promise<ExtensionStudent[]>;
  onCloseModal: () => void;
  onDeleteExtension: (extension: ExtensionAssignment) => Promise<boolean>;
  onExtensionSaved: (savedExtension?: ExtensionAssignment) => Promise<void>;
  resetExtensionErrors: () => void;
  spaceId: string;
  user: ExtensionUser;
};

const ManageExtension = ({
  accessToken,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  createExtension,
  editExtension,
  extension,
  isOpen,
  loadStudents,
  onCloseModal,
  onDeleteExtension,
  onExtensionSaved,
  resetExtensionErrors,
  spaceId,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const deleteStyles = createDeleteStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [titleRef, isTitleOverflowing] = useIsOverflowing<HTMLHeadingElement>();
  const [cohortTitleRef, hasCohortTitleOverflow] =
    useIsOverflowing<HTMLHeadingElement>();
  const dialogRef = useRef<HTMLDivElement>(null);
  const initializedSession = useRef<string>();
  const isEditing = Boolean(extension);
  const [step, setStep] = useState<ManageExtensionStep>('students');
  const [students, setStudents] = useState<ExtensionStudent[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [extensionName, setExtensionName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [gradesReleasedAt, setGradesReleasedAt] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [turnitinAssignment, setTurnitinAssignment] =
    useState<ExtensionAssignment>();
  const [isEditorUpdating, setIsEditorUpdating] = useState(false);
  const [isEditorPersistable, setIsEditorPersistable] = useState(true);
  const [conflict, setConflict] = useState<{
    code: string;
    studentIds: string[];
  }>();
  // Only process Redux create/update errors after an explicit save in this session.
  // Prevents a stale assignmentCreationError from opening the conflict modal on "New extension".
  const shouldProcessOperationErrorsRef = useRef(false);

  useEffect(() => {
    if (isOpen && dialogRef.current) dialogRef.current.focus();
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen) {
      initializedSession.current = undefined;
      return;
    }
    shouldProcessOperationErrorsRef.current = false;
    const sessionKey = JSON.stringify([spaceId, assignment.id, extension?.id]);
    // Refreshing the same assignment must not reset an active Turnitin launch.
    if (initializedSession.current === sessionKey) return;
    initializedSession.current = sessionKey;
    resetExtensionErrors();
    setStep('students');
    setStudents([]);
    setSelectedIds(extension?.students || []);
    setExtensionName(extension?.title || '');
    setDescription(extension?.description ?? assignment.description ?? '');
    setStartDate(extension?.startDate || assignment.startDate || '');
    setEndDate(extension?.endDate || assignment.endDate || '');
    setGradesReleasedAt(
      extension?.gradesReleasedAt || assignment.gradesReleasedAt || ''
    );
    setIsHidden(extension?.isHidden ?? assignment.isHidden ?? false);
    setSearch('');
    setError('');
    setShowDeleteConfirmation(false);
    setTurnitinAssignment(undefined);
    setIsEditorUpdating(false);
    setIsEditorPersistable(true);
    setConflict(undefined);
    setIsLoadingStudents(true);
    loadStudents(spaceId)
      .then((participants) => {
        const currentStudentIds = new Set(participants.map(({ id }) => id));
        setSelectedIds((current) =>
          current.filter((id) => currentStudentIds.has(id))
        );
        setStudents(
          participants.map((student) => ({
            ...student,
            fullName:
              student.fullName ||
              `${student.firstName || ''} ${student.lastName || ''}`.trim(),
          }))
        );
      })
      .catch(() => setError('Students could not be loaded. Please try again.'))
      .finally(() => setIsLoadingStudents(false));
  }, [
    assignment,
    extension,
    isOpen,
    loadStudents,
    resetExtensionErrors,
    spaceId,
  ]);

  useEffect(() => {
    if (!isOpen || !shouldProcessOperationErrorsRef.current) return;
    const operationError = isEditing
      ? assignmentUpdateError
      : assignmentCreationError;
    if (!operationError) return;

    const errorCode =
      typeof operationError.errorCode === 'string'
        ? operationError.errorCode
        : operationError.errorCode?.code;
    const detail =
      operationError.detail ||
      (typeof operationError.errorCode === 'object'
        ? operationError.errorCode.detail
        : undefined);
    if (
      errorCode === 'parent-assignment-submissions-exist' ||
      errorCode === 'extension-submissions-exist'
    ) {
      setConflict({
        code: errorCode,
        studentIds: getConflictStudentIds(detail),
      });
    } else if (
      errorCode === 'extension-of-an-extension' ||
      operationError.errorMessage === 'extension-of-an-extension'
    ) {
      setError('An extension cannot be created from another extension.');
    } else {
      setError('The extension could not be saved. Please try again.');
    }
  }, [assignmentCreationError, assignmentUpdateError, isEditing, isOpen]);

  const selectedStudents = useMemo(
    () => students.filter(({ id }) => selectedIds.includes(id)),
    [selectedIds, students]
  );
  const unselectedStudents = useMemo(
    () => students.filter(({ id }) => !selectedIds.includes(id)),
    [selectedIds, students]
  );
  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return unselectedStudents.filter(
      ({ fullName = '', email = '' }) =>
        fullName.toLowerCase().includes(query) ||
        email.toLowerCase().includes(query)
    );
  }, [search, unselectedStudents]);

  const toggleStudent = (studentId: string) => {
    setSelectedIds((current) =>
      current.includes(studentId)
        ? current.filter((id) => id !== studentId)
        : [...current, studentId]
    );
    setSearch('');
  };

  const buildExtension = (status = 'live') => ({
    ...(extension || {
      description: assignment.description,
      endDate: assignment.endDate,
      gradesReleasedAt: assignment.gradesReleasedAt,
      gradeType: assignment.gradeType,
      isAnonymised: assignment.isAnonymised,
      isHidden: assignment.isHidden,
      provider: assignment.provider,
      shouldCloseOnDueDate: assignment.shouldCloseOnDueDate,
      startDate: assignment.startDate,
    }),
    description,
    endDate: endDate || undefined,
    gradesReleasedAt: gradesReleasedAt || undefined,
    isHidden,
    ...(extension && !extension.objectId && !extension.externalId
      ? { objectId: extension.id }
      : {}),
    parentAssignmentId: extension?.parentAssignmentId || assignment.id,
    startDate: startDate || undefined,
    status,
    students: selectedIds,
    title: extensionName,
  });

  const save = async () => {
    setIsSaving(true);
    setError('');
    shouldProcessOperationErrorsRef.current = true;
    const operation = isEditing ? editExtension : createExtension;
    try {
      const saved = await operation({
        accessToken,
        assignment: buildExtension(),
        currentUser: user,
        spaceId,
      });
      if (saved) {
        await onExtensionSaved(saved);
        if (saved.provider === 'turnitin') {
          setTurnitinAssignment({
            ...saved,
            maxGrade:
              saved.maxGrade ?? extension?.maxGrade ?? assignment.maxGrade,
          });
        } else {
          onCloseModal();
        }
      } else setError('The extension could not be saved. Please try again.');
    } catch (_error) {
      setError('The extension could not be saved. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const closeTurnitin = () => {
    setTurnitinAssignment(undefined);
    onCloseModal();
  };

  const deleteExtension = async () => {
    if (!extension) return;
    setIsSaving(true);
    setError('');
    try {
      const deleted = await onDeleteExtension(extension);
      if (deleted) {
        onCloseModal();
      } else {
        setError('The extension could not be deleted. Please try again.');
      }
    } catch (_error) {
      setError('The extension could not be deleted. Please try again.');
    } finally {
      setIsSaving(false);
      setShowDeleteConfirmation(false);
    }
  };

  const canContinue = Boolean(
    extensionName &&
      !isLoadingStudents &&
      selectedIds.length &&
      !isEditorUpdating &&
      isEditorPersistable
  );
  let modalTitle = 'New extension';
  if (isEditing) {
    modalTitle = extensionName ? `Edit "${extensionName}"` : 'Edit extension';
  }
  if (step === 'properties') {
    modalTitle = extensionName
      ? `Edit properties for '${extensionName}'`
      : 'Edit properties';
  }
  let primaryLabel = 'Next';
  if (step === 'properties') {
    primaryLabel = isEditing ? 'Apply changes' : 'Create extension';
  }

  if (conflict) {
    const affectedStudents = getConflictStudents(conflict.studentIds, students);
    const closeConflict = () => {
      setConflict(undefined);
      shouldProcessOperationErrorsRef.current = false;
      resetExtensionErrors();
      onCloseModal();
    };
    return (
      <ExistingSubmissionErrorDialog
        open={isOpen}
        errorCode={conflict.code}
        onClose={closeConflict}
        provider={assignment.provider}
        students={affectedStudents}
      />
    );
  }

  return (
    <>
      <Modal open={isOpen && !turnitinAssignment} onClose={onCloseModal}>
        <Box
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={
            step === 'cohorts' ? 'Students in other cohorts' : modalTitle
          }
          tabIndex={-1}
          sx={styles.container}
          onKeyDown={(event) => event.key === 'Escape' && onCloseModal()}
        >
          <Box sx={styles.header}>
            {step === 'cohorts' ? (
              <Box
                component="button"
                sx={styles.cohortBackButton}
                onClick={() => setStep('students')}
              >
                <Icons.ArrowBack sx={{ flexShrink: 0 }} />
                <Tooltip
                  title={extensionName ? `Back to ${extensionName}` : 'Back'}
                  disableFocusListener={!isDesktop || !hasCohortTitleOverflow}
                  disableHoverListener={!isDesktop || !hasCohortTitleOverflow}
                  disableTouchListener={!isDesktop || !hasCohortTitleOverflow}
                >
                  <Box
                    key={extensionName ? `Back to ${extensionName}` : 'Back'}
                    ref={cohortTitleRef}
                    sx={styles.cohortBackTitle}
                  >
                    {extensionName ? `Back to ${extensionName}` : 'Back'}
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip
                title={modalTitle}
                disableFocusListener={!isDesktop || !isTitleOverflowing}
                disableHoverListener={!isDesktop || !isTitleOverflowing}
                disableTouchListener={!isDesktop || !isTitleOverflowing}
              >
                <Box
                  key={modalTitle}
                  ref={titleRef}
                  component="h2"
                  sx={styles.title}
                >
                  {modalTitle}
                </Box>
              </Tooltip>
            )}
            <IconButton
              sx={{ flexShrink: 0 }}
              aria-label="Close extension dialog"
              onClick={onCloseModal}
            >
              <Tooltip
                disableFocusListener={!isDesktop}
                disableHoverListener={!isDesktop}
                disableTouchListener={!isDesktop}
                title="Close"
              >
                <Icons.Close sx={{ height: '24px', width: '24px' }} />
              </Tooltip>
            </IconButton>
          </Box>

          <Box
            sx={{
              ...styles.content,
              ...(step !== 'properties' && styles.studentsContent),
            }}
          >
            {step === 'students' && (
              <ExtensionStudentsStep
                extensionName={extensionName}
                isLoading={isLoadingStudents}
                onExtensionNameChange={setExtensionName}
                onOpenCohorts={() => setStep('cohorts')}
                onSearchChange={setSearch}
                onToggleStudent={toggleStudent}
                search={search}
                searchResults={searchResults}
                selectedIds={selectedIds}
                selectedStudents={selectedStudents}
                styles={styles}
                unselectedCount={unselectedStudents.length}
              />
            )}

            {step === 'cohorts' && (
              <CohortStudentsList
                onToggleStudent={toggleStudent}
                selectedIds={selectedIds}
                students={students}
                styles={styles}
                extensionName={extensionName}
              />
            )}

            {step === 'properties' && (
              <ExtensionPropertiesStep
                description={description}
                endDate={endDate}
                gradesReleasedAt={gradesReleasedAt}
                isHidden={isHidden}
                isHandin={assignment.provider === 'hand-in'}
                isTurnitin={assignment.provider === 'turnitin'}
                onDescriptionChange={setDescription}
                onEditorStatusChange={({ isPersistable, isUpdating }) => {
                  setIsEditorPersistable(isPersistable);
                  setIsEditorUpdating(isUpdating);
                }}
                onEndDateChange={setEndDate}
                onGradesReleasedAtChange={setGradesReleasedAt}
                onHiddenChange={setIsHidden}
                onRestore={() => {
                  setDescription(
                    extension?.description ?? assignment.description ?? ''
                  );
                  setStartDate(
                    extension?.startDate || assignment.startDate || ''
                  );
                  setEndDate(extension?.endDate || assignment.endDate || '');
                  setGradesReleasedAt(
                    extension?.gradesReleasedAt ||
                      assignment.gradesReleasedAt ||
                      ''
                  );
                  setIsHidden(
                    extension?.isHidden ?? assignment.isHidden ?? false
                  );
                }}
                onStartDateChange={setStartDate}
                originalDescription={
                  extension?.description ?? assignment.description ?? ''
                }
                originalEndDate={extension?.endDate || assignment.endDate || ''}
                originalGradesReleasedAt={
                  extension?.gradesReleasedAt ||
                  assignment.gradesReleasedAt ||
                  ''
                }
                originalIsHidden={
                  extension?.isHidden ?? assignment.isHidden ?? false
                }
                originalStartDate={
                  extension?.startDate || assignment.startDate || ''
                }
                spaceId={spaceId}
                startDate={startDate}
                styles={styles}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ my: '8px' }}>
                {error}
              </Alert>
            )}
          </Box>

          <Box sx={styles.footer}>
            <Box>
              {isEditing && (
                <Button
                  type="button"
                  kind="secondary"
                  disabled={isSaving}
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  Delete
                </Button>
              )}
            </Box>
            <Box sx={styles.actionButtons}>
              <Button
                type="button"
                kind="secondary"
                onClick={
                  step === 'properties'
                    ? () => setStep('students')
                    : onCloseModal
                }
              >
                {step === 'properties' ? 'Back' : 'Cancel'}
              </Button>
              <Button
                type="button"
                kind="primary"
                loading={isSaving}
                disabled={!canContinue || isSaving}
                onClick={
                  step === 'properties' ? save : () => setStep('properties')
                }
              >
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      {isOpen && showDeleteConfirmation && (
        <DeleteExtensionModal
          styles={deleteStyles}
          showLoader={isSaving}
          open={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={deleteExtension}
        />
      )}
      {isOpen && turnitinAssignment && (
        <TurnitinExtensionModal
          assignment={turnitinAssignment}
          onClose={closeTurnitin}
          spaceId={spaceId}
          user={user}
        />
      )}
    </>
  );
};

export default ManageExtension;
